import React from 'react';
import { Box, Container, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';

export default function ServiceLevelAgreement() {
  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#111827' }}>
          Service Level Agreement (SLA)
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 4 }}>
          Last Updated: October 2025
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Introduction */}
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          This Service Level Agreement ("SLA") describes the level of service that <strong>Sniffr.ai</strong> 
          ("we," "us," or "our") commits to provide to our customers. This SLA applies to all paid subscription 
          plans and outlines our commitments regarding service availability, performance, support, and remedies 
          in the event of service disruptions.
        </Typography>

        {/* Service Availability */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          1. Uptime Guarantee: 99.5%
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          We commit to maintaining a <strong>service uptime of 99.5%</strong> measured on a monthly basis. This translates 
          to a maximum of approximately 3.6 hours of downtime per month, excluding scheduled maintenance windows and 
          force majeure events.
        </Typography>
        <Box sx={{ bgcolor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#1e40af', lineHeight: 1.6, fontFamily: 'monospace' }}>
            <strong>Uptime Calculation:</strong><br/>
            Uptime % = (Total Minutes in Month - Downtime Minutes) / Total Minutes in Month × 100
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          <strong>What Counts as Downtime:</strong> Service is considered unavailable when our core platform features 
          (PR analysis, dashboard access, integrations) are not accessible to users. Scheduled maintenance, 
          partial degradation, and third-party service failures do not count toward downtime.
        </Typography>

        {/* Support Hours and Channels */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          2. Support Hours & Channels
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          We provide <strong>Hybrid Support</strong> with different coverage levels based on issue severity:
        </Typography>
        <Box component="ul" sx={{ color: '#374151', mb: 3, lineHeight: 1.8, pl: 4 }}>
          <li><strong>Critical Issues (Platform Down):</strong> 24/7 emergency support via all channels</li>
          <li><strong>Normal Support:</strong> Monday–Friday, 9:00 AM to 6:00 PM IST (Indian Standard Time)</li>
          <li><strong>After-Hours:</strong> Emergency hotline available for Enterprise customers</li>
        </Box>

        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          <strong>Available Support Channels:</strong>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
          <Chip label="📧 Email (support@sniffr.ai)" variant="outlined" />
          <Chip label="💬 Support Portal (In-App)" variant="outlined" />
          <Chip label="📞 WhatsApp (Enterprise)" variant="outlined" />
          <Chip label="💼 Slack (Enterprise)" variant="outlined" />
        </Box>

        {/* Incident Response & Resolution Times */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          3. Incident Response & Resolution Times
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          Our support team commits to the following response and resolution timeframes:
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 4, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#1f2937', bgcolor: '#f9fafb' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1f2937', bgcolor: '#f9fafb' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1f2937', bgcolor: '#f9fafb' }}>Response Time</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1f2937', bgcolor: '#f9fafb' }}>Resolution Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ color: '#ef4444', fontWeight: 600 }}>Critical</TableCell>
                <TableCell sx={{ color: '#374151' }}>Platform completely down or major functionality unavailable</TableCell>
                <TableCell sx={{ color: '#374151' }}>Within 2 hours</TableCell>
                <TableCell sx={{ color: '#374151' }}>Within 12 hours</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: '#f97316', fontWeight: 600 }}>High</TableCell>
                <TableCell sx={{ color: '#374151' }}>Significant feature unavailable or severely degraded</TableCell>
                <TableCell sx={{ color: '#374151' }}>Within 4 hours</TableCell>
                <TableCell sx={{ color: '#374151' }}>Within 24 hours</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: '#fbbf24', fontWeight: 600 }}>Medium</TableCell>
                <TableCell sx={{ color: '#374151' }}>Minor feature issue or performance degradation</TableCell>
                <TableCell sx={{ color: '#374151' }}>Within 1 business day</TableCell>
                <TableCell sx={{ color: '#374151' }}>Within 3-5 business days</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: '#22c55e', fontWeight: 600 }}>Low</TableCell>
                <TableCell sx={{ color: '#374151' }}>General inquiries, feature requests, cosmetic issues</TableCell>
                <TableCell sx={{ color: '#374151' }}>Within 2 business days</TableCell>
                <TableCell sx={{ color: '#374151' }}>Best effort basis</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Service Credits */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          4. Service Credits (Yes - We Offer Credits for SLA Breaches)
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          If we fail to meet our uptime commitment, you may be eligible for service credits as follows:
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 2, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Monthly Uptime Percentage</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Service Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ color: '#374151' }}>Below 99.5% but above 99.0%</TableCell>
                <TableCell sx={{ color: '#374151' }}>5% of monthly subscription fee</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: '#374151' }}>Below 99.0% but above 98.0%</TableCell>
                <TableCell sx={{ color: '#374151' }}>10% of monthly subscription fee</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: '#374151' }}>Below 98.0% but above 95.0%</TableCell>
                <TableCell sx={{ color: '#374151' }}>25% of monthly subscription fee</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ color: '#374151' }}>Below 95.0%</TableCell>
                <TableCell sx={{ color: '#374151' }}>50% of monthly subscription fee</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          <strong>Claiming Service Credits:</strong> To receive a service credit, you must submit a claim within 
          30 days of the incident via email at support@sniffr.ai with evidence of the outage. Service credits will 
          be applied to your next billing cycle and cannot be redeemed for cash.
        </Typography>
        <Box sx={{ bgcolor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 1, p: 2, mb: 4 }}>
          <Typography variant="body2" sx={{ color: '#92400e', lineHeight: 1.6 }}>
            <strong>⚠️ Note:</strong> Service credits are the sole and exclusive remedy for any service availability 
            failures. Maximum aggregate credits per month cannot exceed 50% of monthly subscription fee.
          </Typography>
        </Box>

        {/* Additional Terms */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          5. Additional Terms & Exclusions
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          This SLA does not apply to service unavailability caused by:
        </Typography>
        <Box component="ul" sx={{ color: '#374151', mb: 2, lineHeight: 1.8, pl: 4 }}>
          <li>Scheduled maintenance performed with proper 48-hour advance notice</li>
          <li>Force majeure events (natural disasters, war, terrorism, pandemics, etc.)</li>
          <li>Third-party service failures (GitHub, Bitbucket, Azure DevOps, GitLab, Jira outages)</li>
          <li>Customer's equipment, software, internet connectivity, or network issues</li>
          <li>Actions or inactions by the customer or any third party</li>
          <li>Suspension due to violation of Terms of Service or non-payment</li>
          <li>Free trial, free tier, or beta/experimental features</li>
        </Box>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          <strong>Scheduled Maintenance:</strong> We perform scheduled maintenance during low-usage periods (typically 
          weekends, 10 PM - 2 AM IST). Maintenance windows are announced 48 hours in advance and don't count toward downtime.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* General Terms */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          6. General Terms
        </Typography>
        
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1f2937', fontSize: '1rem' }}>
          SLA Modifications
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 3, lineHeight: 1.8 }}>
          We reserve the right to modify this SLA with 30 days advance notice via email. Continued use of services 
          after modifications constitutes acceptance. Enterprise customers may negotiate custom SLA terms.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1f2937', fontSize: '1rem' }}>
          Dispute Resolution
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          Any disputes arising from this SLA will first be addressed through good-faith negotiation. If unresolved, 
          disputes will be settled through arbitration in Chennai, India, under Indian law.
        </Typography>

        {/* Contact Information */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          7. Contact Information
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          For questions about this SLA, to report service issues, or to submit a service credit claim, please contact us:
        </Typography>
        <Box sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 1, p: 3, mb: 4 }}>
          <Typography variant="body1" sx={{ color: '#374151', mb: 1, lineHeight: 1.8 }}>
            <strong>Support Email:</strong> support@sniffr.ai
          </Typography>
          <Typography variant="body1" sx={{ color: '#374151', mb: 1, lineHeight: 1.8 }}>
            <strong>Sales Inquiries:</strong> sales@sniffr.ai
          </Typography>
          <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.8 }}>
            <strong>Website:</strong> https://sniffr.ai
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
            © 2025 Sniffr.ai. All rights reserved.
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
            This SLA is effective from October 2025 and supersedes all previous versions
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

